// src/pages/contrato/ContratoBluelt.tsx
// Tela do card "Contrato Bluelt". Exibe os dados de entitlement M2M já carregados
// pelo EntitlementContext (nenhuma chamada nova de backend): status do contrato,
// limites contratados (lojas/usuários) e módulos contratados.
//
// Gate: permissão granular de usuário `modulo.contrato_bluelt` (aba "Bluelt" do
// form de usuários). NÃO é gate de módulo de tenant. Três camadas: card na Home,
// ProtectedRoute (URL direta) e este guard in-page.
import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract } from '@/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlement } from '../../contexts/EntitlementContext';
import { moduleLabel } from '../../config/moduleLabels';
import './ContratoBluelt.css';

// Rótulos das chaves de limite conhecidas do claim `limits`. Chave desconhecida
// cai no fallback (mostra a própria chave), sem esconder dado do M2M.
const LIMIT_LABELS: Record<string, string> = {
  LIMITE_FILIAIS: 'Limite de Lojas',
  LIMITE_USUARIOS_TOTAL: 'Limite de Usuários',
};

// Ordem fixa dos limites conhecidos; extras entram depois, ordenados.
const KNOWN_LIMIT_ORDER = ['LIMITE_FILIAIS', 'LIMITE_USUARIOS_TOTAL'];

// Rótulo amigável do status efetivo (claim effectiveStatus).
const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
  BLOCKED: 'Bloqueado',
};

const ContratoBluelt = () => {
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const { modules, limits, effectiveStatus, status } = useEntitlement();

  useEffect(() => {
    document.title = 'Bluelt | Contrato';
  }, []);

  // Guard in-page (mesma permissão do card e do ProtectedRoute).
  if (!hasPermission('modulo.contrato_bluelt')) {
    return <div>Você não tem permissão para acessar esta página.</div>;
  }

  const statusLabel = effectiveStatus
    ? STATUS_LABELS[effectiveStatus] ?? effectiveStatus
    : '—';
  const statusClass = `status-${(effectiveStatus ?? '').toLowerCase()}`;

  // Limites: conhecidos primeiro (ordem fixa), depois os extras ordenados.
  const limitKeys = [
    ...KNOWN_LIMIT_ORDER.filter((k) => k in limits),
    ...Object.keys(limits)
      .filter((k) => !KNOWN_LIMIT_ORDER.includes(k))
      .sort(),
  ];

  return (
    <div className={`contrato-page ${theme}`}>
      <main className="contrato-main">
        <h1>
          <FontAwesomeIcon icon={faFileContract} /> Contrato Bluelt
        </h1>
      </main>

      {status === 'loading' || status === 'idle' ? (
        <p>Carregando...</p>
      ) : status === 'error' ? (
        <p className="error-message">
          Não foi possível carregar os dados do contrato. Tente novamente.
        </p>
      ) : (
        <div className="contrato-content">
          <section className="contrato-section contrato-status-row">
            <span className="contrato-status-label">Status do contrato:</span>
            <span className={`contrato-status-badge ${statusClass}`}>{statusLabel}</span>
          </section>

          <section className="contrato-section">
            <h2>Limites contratados</h2>
            {limitKeys.length === 0 ? (
              <p>Nenhum limite definido.</p>
            ) : (
              <div className="contrato-tiles">
                {limitKeys.map((k) => (
                  <div key={k} className="contrato-tile">
                    <span className="contrato-tile-value">{limits[k]}</span>
                    <span className="contrato-tile-label">{LIMIT_LABELS[k] ?? k}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="contrato-section">
            <h2>Módulos contratados</h2>
            {modules.length === 0 ? (
              <p>Nenhum módulo contratado.</p>
            ) : (
              <div className="contrato-modules">
                {[...modules].sort().map((code) => (
                  <span key={code} className="contrato-module-chip">
                    {moduleLabel(code)}
                  </span>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default ContratoBluelt;
